// src/utils/pluralRu.ts

/**
 * Правильное склонение русских существительных по числительным
 * @param n - число (количество)
 * @param forms - массив форм [one, few, many]
 *   - one: 1, 21, 31, 41... (один, одна, одно)
 *   - few: 2-4, 22-24, 32-34... (два, три, четыре)
 *   - many: 5-20, 25-30... (пять, десять, двадцать)
 * @returns правильная форма слова
 *
 * @example
 * pluralRu(1, ['день', 'дня', 'дней']) // "день"
 * pluralRu(2, ['день', 'дня', 'дней']) // "дня"
 * pluralRu(5, ['день', 'дня', 'дней']) // "дней"
 * pluralRu(11, ['день', 'дня', 'дней']) // "дней"
 * pluralRu(21, ['день', 'дня', 'дней']) // "день"
 * pluralRu(22, ['день', 'дня', 'дней']) // "дня"
 * pluralRu(25, ['день', 'дня', 'дней']) // "дней"
 */
export function pluralRu(n: number, forms: [one: string, few: string, many: string]): string {
  const absN = Math.abs(n);
  const mod100 = absN % 100;
  const mod10 = absN % 10;

  // Числа от 11 до 19 всегда используют форму "many"
  if (mod100 >= 11 && mod100 <= 19) {
    return forms[2]; // many
  }

  // Числа, оканчивающиеся на 1 (кроме 11)
  if (mod10 === 1) {
    return forms[0]; // one
  }

  // Числа, оканчивающиеся на 2-4 (кроме 12-14)
  if (mod10 >= 2 && mod10 <= 4) {
    return forms[1]; // few
  }

  // Все остальные случаи (0, 5-9, 10-20, ...)
  return forms[2]; // many
}

/**
 * Форматирует число с правильным склонением слова "день"
 * @param count - количество дней
 * @returns строка вида "N день/дня/дней"
 *
 * @example
 * formatDays(1) // "1 день"
 * formatDays(5) // "5 дней"
 * formatDays(21) // "21 день"
 */
export function formatDays(count: number): string {
  return `${count} ${pluralRu(count, ['день', 'дня', 'дней'])}`;
}

/**
 * Возвращает только правильную форму слова "день"
 * @param count - количество дней
 * @returns "день", "дня" или "дней"
 */
export function getDaysWord(count: number): string {
  return pluralRu(count, ['день', 'дня', 'дней']);
}
